/**
 * Single source of truth for onboarding field-level requirements.
 *
 * Two layers used to define "this step is done" independently:
 *  - each page's `validateForm` (gates the Save button)
 *  - `utils/onboardingProgress.ts` (gates the sidebar, resume routing, and
 *    the Review submit gate)
 *
 * They drifted, and the looser one won: `isBusinessCompanyDetailsComplete`
 * checked only companyName + countryOfIncorporation, so an application missing
 * `annualRevenue` was marked complete, skipped by `getResumeStep`, and
 * submitted from Review with the field blank.
 *
 * Every threshold and predicate a form enforces lives here so both layers
 * import the same rule. Adding a required field means editing this file, not
 * two files that can disagree.
 */

// ---------------------------------------------------------------------------
// Length thresholds (shared with the page-level `minLength` UI hints)
// ---------------------------------------------------------------------------

export const BUSINESS_DESCRIPTION_MIN_LENGTH = 200;
export const FLOW_OF_FUNDS_MIN_LENGTH = 50;
export const OTHER_PURPOSE_MIN_LENGTH = 50;

// ---------------------------------------------------------------------------
// Primitive field predicates
// ---------------------------------------------------------------------------

/** A required text field is filled when it is a non-blank string. */
export function isFilled(value: unknown): boolean {
  return typeof value === "string" && value.trim() !== "";
}

/**
 * A required amount field is filled when it is a real number above zero.
 *
 * `> 0` (not `!= null`) is deliberate: it mirrors `Number(annualRevenue) > 0`
 * on the company-details form. A blank number input coerces to `0`, so an
 * `!= null` check would treat a blank field as answered.
 */
export function isPositiveAmount(value: unknown): boolean {
  return typeof value === "number" && Number.isFinite(value) && value > 0;
}

/** A required multi-select is filled when at least one option is chosen. */
export function isNonEmptyList(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0;
}

/** A required free-text field that carries a minimum-length rule. */
export function meetsMinLength(value: unknown, min: number): boolean {
  return typeof value === "string" && value.trim().length >= min;
}

/**
 * An address is complete when every field the address forms mark required is
 * present. `line2` and `state` are deliberately excluded — both render with an
 * "(optional)" suffix and neither form validates them.
 */
export function isAddressComplete(address: unknown): boolean {
  if (typeof address !== "object" || address === null) return false;
  const addr = address as Record<string, unknown>;
  return (
    isFilled(addr.line1) &&
    isFilled(addr.postTown) &&
    isFilled(addr.postCode) &&
    isFilled(addr.country)
  );
}

// ---------------------------------------------------------------------------
// Business description codec
// ---------------------------------------------------------------------------

export const COMPANY_STRUCTURE_VALUES = [
  "simple",
  "medium",
  "complex",
] as const;
export type CompanyStructure = (typeof COMPANY_STRUCTURE_VALUES)[number];

const STRUCTURE_PREFIX = "Company Structure: ";

/**
 * The company-details step collects "company structure" and "business
 * description" as two inputs but persists them in the single
 * `businessDetails.businessDescription` string the API exposes. Encode/decode
 * live here so the form, the Review summary, and the completion predicate all
 * agree on the wire format.
 */
export function encodeBusinessDescription(
  structure: string,
  description: string
): string | undefined {
  const parts: string[] = [];
  if (structure) {
    parts.push(`${STRUCTURE_PREFIX}${structure}`);
  }
  if (description.trim()) {
    parts.push(description.trim());
  }
  return parts.length > 0 ? parts.join("\n\n") : undefined;
}

export function decodeBusinessDescription(raw: string | undefined): {
  structure: string;
  description: string;
} {
  if (!raw) return { structure: "", description: "" };

  if (raw.startsWith(STRUCTURE_PREFIX)) {
    const afterPrefix = raw.slice(STRUCTURE_PREFIX.length);
    const separatorIdx = afterPrefix.indexOf("\n\n");
    if (separatorIdx === -1) {
      // Only structure, no description body
      return { structure: afterPrefix.trim(), description: "" };
    }
    return {
      structure: afterPrefix.slice(0, separatorIdx).trim(),
      description: afterPrefix.slice(separatorIdx + 2).trim(),
    };
  }

  return { structure: "", description: raw };
}

/**
 * The company-details form requires BOTH a structure selection and a
 * description body of at least `BUSINESS_DESCRIPTION_MIN_LENGTH` characters.
 */
export function isBusinessDescriptionComplete(raw: unknown): boolean {
  if (typeof raw !== "string") return false;
  const { structure, description } = decodeBusinessDescription(raw);
  return (
    isFilled(structure) &&
    meetsMinLength(description, BUSINESS_DESCRIPTION_MIN_LENGTH)
  );
}
